import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Trash2, Edit2 } from 'lucide-react';
import { api } from '@/core/apiClient';

interface Role {
  id: string;
  name: string;
}

interface User {
  id: string;
  email: string;
  username?: string | null;
  firstName: string;
  lastName: string;
  phone?: string;
  roleId: string;
  brandId?: string;
  role?: Role;
  isAdminFullAccess: boolean;
  isActive: number;
  createdAt: string;
  companyIds?: number[];
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<{ success: boolean; data: User[] }>('/admin/users');
      setUsers(response.data || []);
    } catch (err) {
      console.error('[AdminUsers] Error loading users:', err);
      setError("Error en carregar els usuaris");
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.username ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.lastName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (userId: string) => {
    if (!window.confirm("Estàs segur que vols eliminar aquest usuari?")) return;

    try {
      const response = await api.delete<{ success: boolean }>(`/admin/users/${userId}`);
      if (response.success) {
        setUsers(users.filter((u) => u.id !== userId));
      }
    } catch (err) {
      console.error('[AdminUsers] Error deleting user:', err);
      setError("Error en eliminar l'usuari");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Usuaris</h1>
          <p className="text-muted-foreground mt-1">Gestió d'usuaris del sistema</p>
        </div>
        <button
          onClick={() => navigate('/admin/users/new')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium"
        >
          <Plus className="w-4 h-4" />
          Nou usuari
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Cercar per email, nom..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 text-red-500 rounded-lg border border-red-500/20">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">Usuari</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">Rol</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">Estat</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-foreground">Accions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                    Carregant usuaris...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                    No hi ha usuaris per mostrar
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-border hover:bg-muted/50 cursor-pointer"
                    onClick={() => navigate(`/admin/users/${u.id}/edit`)}
                  >
                    <td className="px-6 py-4 text-xs text-foreground">{u.email}</td>
                    <td className="px-6 py-4 text-xs font-mono text-foreground">{u.username ?? <span className="text-muted-foreground italic text-xs">—</span>}</td>
                    <td className="px-6 py-4 text-xs text-foreground">
                      {u.firstName} {u.lastName}
                    </td>
                    <td className="px-6 py-4 text-xs">
                      <div className="flex items-center gap-2">
                        {u.role?.name && (
                          <span className="inline-block px-2 py-1 bg-muted rounded text-xs font-medium">
                            {u.role.name}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          u.isActive ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-500'
                        }`}
                      >
                        {u.isActive ? 'Actiu' : 'Inactiu'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/admin/users/${u.id}/edit`); }}
                          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(u.id); }}
                          className="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">Total: {filteredUsers.length} usuaris</div>
    </div>
  );
}
